import { useEffect, useRef } from "react";
import type { OcSessionEndReason } from "@/lib/oc-api";

export interface OcSessionExpiryDialogProps {
  open: boolean;
  /** Milliseconds left before sign-out. Re-rendered every second by the caller. */
  remainingMs: number;
  /** Which clock is about to fire. */
  reason: OcSessionEndReason;
  /**
   * Whether anything can still be extended: the idle clock always can, the
   * server clock only until the session reaches its renewal cap.
   */
  canRenew: boolean;
  /** An admin acting as a tenant user — they cannot "sign in again" here. */
  isImpersonation: boolean;
  /** A renewal is in flight; the button waits rather than letting it be clicked twice. */
  isRenewing?: boolean;
  onStaySignedIn: () => void;
  /** "Sign out now" — end the session immediately. */
  onSignOut: () => void;
}

function ocFormatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Warns before the portal signs the user out and offers to keep the session
 * alive. Deliberately not dismissible — no Escape, no backdrop click — because
 * dismissing it would look like "stay signed in" while the clock kept running.
 * The only ways out are the two buttons or the countdown reaching zero.
 */
/**
 * What the dialog says. The end-of-session case differs by session type: an
 * impersonating admin has no password for this tenant, so telling them to sign
 * in again would send them to a login screen they cannot get through.
 */
function ocExpiryCopy(
  reason: OcSessionEndReason,
  canRenew: boolean,
  isImpersonation: boolean
): { title: string; body: string } {
  if (canRenew) {
    if (isImpersonation) {
      return {
        title: "This impersonation window is about to end",
        body: "You can extend it, up to the maximum length for an impersonation session.",
      };
    }
    return {
      title: "Your session is about to expire",
      body:
        reason === "inactivity"
          ? "You have been inactive for a while. For security, you will be signed out automatically."
          : "Your session is close to its time limit. You can extend it and keep working.",
    };
  }

  if (isImpersonation) {
    return {
      title: "Impersonation window ended",
      body: "This impersonation session has reached its maximum length and cannot be extended. To keep working, start a new one from the admin panel.",
    };
  }
  return {
    title: "Your session is ending",
    body: "You have reached the maximum session length. For security, you will be signed out and will need to sign in again.",
  };
}

export function OcSessionExpiryDialog({
  open,
  remainingMs,
  reason,
  canRenew,
  isImpersonation,
  isRenewing = false,
  onStaySignedIn,
  onSignOut,
}: OcSessionExpiryDialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    primaryRef.current?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Keep Tab inside the panel; there is nothing behind it the user may reach.
  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const activeEl = document.activeElement;
    if (e.shiftKey) {
      if (activeEl === first || !panel.contains(activeEl)) {
        e.preventDefault();
        last.focus();
      }
    } else if (activeEl === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  const countdown = ocFormatCountdown(remainingMs);
  const copy = ocExpiryCopy(reason, canRenew, isImpersonation);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onKeyDown={handlePanelKeyDown}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="oc-session-expiry-title"
        aria-describedby="oc-session-expiry-desc"
        className="w-full max-w-md rounded-lg border border-slate-200 dark:border-oc-navy-700 bg-white dark:bg-oc-navy-800 shadow-xl"
      >
        <div className="p-6">
          <h2
            id="oc-session-expiry-title"
            className="text-base font-semibold text-slate-900 dark:text-slate-100"
          >
            {copy.title}
          </h2>

          <p
            id="oc-session-expiry-desc"
            className="mt-2 text-sm text-slate-600 dark:text-slate-400"
          >
            {copy.body}
          </p>

          <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Signing out in{" "}
              {/* Announce roughly once a minute rather than on every tick, so a
                  screen reader is not flooded with seconds. */}
              <span
                className="font-mono font-semibold tabular-nums"
                role="timer"
                aria-live="off"
              >
                {countdown}
              </span>
            </p>
            <span className="sr-only" aria-live="polite">
              {remainingMs > 60_000
                ? `Signing out in about ${Math.round(remainingMs / 60_000)} minutes.`
                : "Signing out in less than a minute."}
            </span>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onSignOut}
              ref={canRenew ? undefined : primaryRef}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-oc-navy-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-oc-navy-700 transition-colors"
            >
              Sign out now
            </button>
            {canRenew && (
              <button
                type="button"
                ref={primaryRef}
                onClick={onStaySignedIn}
                disabled={isRenewing}
                className="px-4 py-2 text-sm font-medium rounded-md bg-oc-teal-600 text-white hover:bg-oc-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isRenewing ? "Extending…" : "Stay signed in"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OcSessionExpiryDialog;
